
import { Component, Prop, Vue } from 'vue-property-decorator';
import { PERMISSION } from '@/permissions';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { cleanAvailabilities, initTempAvailability } from '../../utils/availability';
import { formatMenu } from '../../utils/format';
import { scrollToFirstError } from '@/utils/errorHandling';
import '@/validation-rules';
import Toast from '@/utils/toast';
import NameAndPricing from '@/components/shared/form/NameAndPricing.vue';
import FileUpload from '@/components/shared/form/FileUpload.vue';
import Tooltip from '@/components/shared/Tooltip.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import Availability from './form/Availability.vue';
import MenuType from './form/MenuType.vue';
import MenuLocaleEditor from './form/MenuLocaleEditor.vue';
import ExpansionPanel from '@/components/shared/suites/panels/ExpansionPanel.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import RichTextEditor from '@/components/shared/RichTextEditor.vue';

const namespace: string = 'menus';

@Component<MenuEditor>({
	components: {
		NameAndPricing,
		FileUpload,
		Availability,
		MenuType,
		MenuLocaleEditor,
		Tooltip,
		StandardModal,
		ExpansionPanel,
		ToggleSwitch,
		ValidationObserver,
		RichTextEditor
	}
})
export default class MenuEditor extends Vue {
	@Action('createMenu', { namespace }) private createMenu!: (payload: object) => Promise<void>;
	@Action('updateMenu', { namespace }) private updateMenu!: (payload: object) => Promise<void>;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Prop({ type: Boolean, default: true }) private isCreating!: boolean;
	@Prop({ type: Boolean, default: true }) private disabled!: boolean;
	@Prop({ type: Object, default: () => {} }) private menu!: Menu;
	@Prop({ type: Object, default: () => {} }) private selectedLocale!: RestaurantLocale;

	$refs!: { observer: Validate; };

	private PERMISSION = PERMISSION;

	// TODO: mutableMenu copies menu by reference instead of by value (cloneDeep),
	// so we can't compare the two to detect changes. Fix this to eliminate snapshot variables.
	private mutableMenu: Menu = this.menu;
	private loading: boolean = false;

	private initialAllowSectionsValue = this.menu.allow_sections;
	private initialAvailability: string = JSON.stringify(cleanAvailabilities(this.menu.availability ?? []));
	private showDisableSectionsConfirmModal: boolean = false;
	private showEnableSectionsConfirmModal: boolean = false;
	private isAvailabilitySelected: boolean = false;
	private menuTypeError: boolean = false;
	private availabilityError: boolean = false;

	// Image upload
	private S3_BUCKET_BASE_URL: string = process.env.VUE_APP_S3_BUCKET_BASE_URL;
	private mutableImageFile: string | null = null;

	private get valid(): boolean {
		return !!this.mutableMenu.name
			// && !!this.mutableMenu.type **TEMPORARY DEFAULT TO A LA CARTE**
			&& (this.mutableMenu.dine_in
				|| this.mutableMenu.on_demand
				|| this.mutableMenu.scheduled)
			&& (this.isK12Location || this.isAvailabilitySelected);
		// && (this.mutableMenu.type === 'a-la-carte'
		// 	|| (!isFalseyButNotZero(this.mutableMenu.price)
		// 		&& Number(this.mutableMenu.price) >= 0));
	}

	private get sectionsDisabled(): boolean {
		return !this.mutableMenu.allow_sections;
	}

	private set sectionsDisabled(value: boolean) {
		this.mutableMenu.allow_sections = !value;
	}

	private get memberOnly(): boolean {
		return this.mutableMenu.member_only;
	}

	private set memberOnly(value: boolean) {
		this.mutableMenu.member_only = value;
	}

	/**
	 * Set default values if we are creating a menu.
	 * Check menu hours are selected when editing.
	 *
	 * @return {void}
	 */
	private created(): void {
		if (this.isCreating) {
			this.mutableMenu.allow_sections = true;
			Vue.set(this.mutableMenu, 'dine_in', false);

			if (this.isK12Location) {
				this.mutableMenu.scheduled = true;

				// Set 24hr availability for K12 locations
				const availability = initTempAvailability().map((avail: MenuAvailability) => ({
					...avail,
					start_time: '00:00',
					end_time: '00:00'
				}));
				Vue.set(this.mutableMenu, 'availability', availability);
				this.isAvailabilitySelected = true;
			}
			else if (this.isCateringRestaurant) {
				this.mutableMenu.scheduled = true;
				this.mutableMenu.max_orders_per_interval = 0;
				this.mutableMenu.required_notice = 48 * 60;
				this.mutableMenu.ordering_interval = 15;
			}
		}
		else {
			this.checkAvailabilityIsSelected();
		}
	}

	/**
	 * Notify the parent that all initialization-time menu mutations (defaults
	 * applied in created/mounted hooks of this editor and its children) are done,
	 * so it can capture the change-detection snapshot.
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.$emit('editor-ready');
	}

	/**
	 * Trigger when availability are updated, make sure a start and end date are selected to enable the save button.
	 * * The logic is a workaround as this.mutableMenu.availability changes are not being detected by the valid computed property.
	 *
	 * @return {void}
	 */
	private checkAvailabilityIsSelected(): void {
		this.isAvailabilitySelected = this.mutableMenu.availability?.some((avail: MenuAvailability) =>avail.start_time && avail.end_time) ?? false;
		this.availabilityError = false;
	}

	/**
	 * Makes necessary checks for warnings or confirmation modals before
	 * saving. If nothing else needs to be shown, saves immediately
	 *
	 * @return {Promise<void>}
	 */
	private async preSave (): Promise<void> {
		this.menuTypeError = false;
		this.availabilityError = false;

		const isValid = await this.$refs.observer.validate();

		const hasMenuType = this.mutableMenu.dine_in || this.mutableMenu.on_demand || this.mutableMenu.scheduled;
		if (!hasMenuType) {
			this.menuTypeError = true;
		}

		if (!this.isK12Location && !this.isAvailabilitySelected) {
			this.availabilityError = true;
		}

		if (!isValid || this.menuTypeError || this.availabilityError) {
			Toast.error(this.$t('shared.error_missing_required_fields'));
			scrollToFirstError();
			return;
		}

		if (!this.isCreating && this.initialAllowSectionsValue !== this.mutableMenu.allow_sections) {
			const hasItems: boolean = !!this.mutableMenu?.items?.length;
			const hasSections: boolean = !!this.mutableMenu?.sections?.length;
			if (this.mutableMenu.allow_sections && hasItems) {
				this.showEnableSectionsConfirmModal = true;
			}
			else if (hasSections) {
				this.showDisableSectionsConfirmModal = true;
			}
			else {
				this.save();
			}
		}
		else {
			this.save();
		}
	}

	/**
	 * Toggle operator only menu status
	 *
	 * @return {void}
	 */
	private updateMenuAccessLevel(): void {
		this.mutableMenu.access_level = this.mutableMenu.access_level === 'operator' ? 'unrestricted' : 'operator';
	}

	/**
	 * Closes off all confirmation modals
	 *
	 * @return {void}
	 */
	private cancelConfirmModal (): void {
		this.showEnableSectionsConfirmModal = false;
		this.showDisableSectionsConfirmModal = false;
	}

	/**
	 * Save the menu (either create or update)
	 *
	 * @return {Promise<void>}
	 */
	private async save(): Promise<void> {
		this.loading = true;
		if (this.isCreating) {
			await this.createMenu({
				menu: formatMenu(this.mutableMenu),
				availability: cleanAvailabilities(this.mutableMenu.availability!),
				imageFile: this.mutableImageFile
			})
				.then(() => {
					this.$emit('close-modal', true);
				})
				.catch((errorMessage) => {
					Toast.error(this.$t('menu.error_creating_menu', { menuName: this.mutableMenu.name, errorMessage }));
				});
		}
		else {
			const cleanedAvailability = cleanAvailabilities(this.mutableMenu.availability ?? []);
			const availabilityChanged = JSON.stringify(cleanedAvailability) !== this.initialAvailability;

			await this.updateMenu({
				menu: formatMenu(this.mutableMenu),
				...(availabilityChanged && { availability: cleanedAvailability }),
				imageFile: this.mutableImageFile
			})
				.then(() => {
					this.$emit('close-modal', true);
				})
				.catch((errorMessage) => {
					Toast.error(this.$t('menu.error_updating_menu', { menuName: this.mutableMenu.name, errorMessage }));
				});
		}
		this.loading = false;
	}
}
